import type { ReactNode } from "react";

// Guide content lives here. Each entry is a self-contained article:
// metadata (slug, title, description, lastUpdated) + a JSX body with
// section headings whose `id`s match the table of contents. Adding a
// new guide is a matter of appending one object — the routing,
// listing, sitemap (regenerated at build time), and meta-tag plumbing
// in App.tsx pick it up automatically by slug.

export interface GuideSection {
  id: string;
  label: string;
}

export interface Guide {
  slug: string;
  title: string;
  // Used for <meta description>, og:description, the index card, and
  // JSON-LD `description`. Keep at or below 155 chars so SERP snippets
  // aren't truncated.
  description: string;
  // Visible on the index card under the description.
  summary: string;
  // ISO yyyy-mm-dd. Surfaces as "Last updated" + JSON-LD dateModified.
  lastUpdated: string;
  sections: GuideSection[];
  body: () => ReactNode;
}

// Inline mid-page CTA — a single muted line with a link, dropped
// after the section that most naturally introduces CaseThread. Not
// salesy, just a pointer.
function InlineCta({ children }: { children: ReactNode }) {
  return (
    <p className="guide-inline-cta">
      <span aria-hidden>→ </span>
      {children}
    </p>
  );
}

export const GUIDES: Guide[] = [
  // ------------------------------------------------------------------
  {
    slug: "denied-insurance-claim-appeal",
    title: "How to Appeal a Denied Insurance Claim",
    description:
      "A practical step-by-step guide to appealing a denied insurance claim: read the denial, gather evidence, write the letter, and escalate if needed.",
    summary:
      "Your insurer said no. Here is how to read the denial, build a documented response, and escalate to your state insurance commissioner if the appeal goes nowhere.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "review", label: "1. Review the denial letter carefully" },
      { id: "reason", label: "2. Understand the stated reason" },
      { id: "documents", label: "3. Gather supporting documents" },
      { id: "appeal-letter", label: "4. Write the appeal letter" },
      { id: "track", label: "5. Track every piece of correspondence" },
      { id: "organize", label: "Staying organized during your appeal" },
      { id: "escalate", label: "6. Escalate to the regulator if needed" },
      { id: "timeline", label: "What to expect on timing" },
    ],
    body: () => (
      <>
        <p>
          A denied claim is rarely the end of the conversation. Most insurers
          have a formal internal appeal process, and most states give you the
          right to escalate to an external regulator if that appeal fails.
          The biggest predictor of success is not the strength of your
          original claim — it is how organized your response is.
        </p>

        <h2 id="review">1. Review the denial letter carefully</h2>
        <p>
          The denial letter is your roadmap. Read it twice. It must, by law in
          most jurisdictions, state the specific reason for denial and the
          specific policy clause it relies on. Note the deadline to appeal
          (often 30, 60, or 180 days), the exact mailing address or portal,
          and whether the insurer requires a particular form.
        </p>
        <p>
          If the letter is vague — "claim does not meet policy requirements"
          with no specifics — that is itself appealable. Write back and
          request the specific policy provision and the specific facts the
          insurer relied on. Keep that request in writing.
        </p>

        <h2 id="reason">2. Understand the stated reason</h2>
        <p>Common denial reasons fall into a few buckets:</p>
        <ul>
          <li>
            <strong>Excluded loss</strong> — the policy does not cover this
            type of damage or event. Re-read the exclusions section yourself.
          </li>
          <li>
            <strong>Insufficient documentation</strong> — the insurer needs
            more proof of loss. This is often the easiest to fix.
          </li>
          <li>
            <strong>Late notice</strong> — you reported the claim after a
            deadline. Check the policy's exact reporting requirement.
          </li>
          <li>
            <strong>Pre-existing condition</strong> (health) or{" "}
            <strong>wear and tear</strong> (property) — the loss predates
            coverage or is considered maintenance, not damage.
          </li>
          <li>
            <strong>Coverage limit reached</strong> — you have already used
            your annual or per-incident maximum.
          </li>
        </ul>
        <p>
          Match the stated reason to the actual policy text. Insurers
          sometimes cite a clause that does not say what they claim it says.
        </p>

        <h2 id="documents">3. Gather supporting documents</h2>
        <p>Pull together everything that contradicts or weakens the denial:</p>
        <ul>
          <li>The original policy document and any endorsements.</li>
          <li>
            All correspondence with the insurer (emails, letters, portal
            messages, call logs).
          </li>
          <li>
            Independent evidence — repair estimates, photographs, medical
            records, expert reports, receipts, witness statements.
          </li>
          <li>
            For health claims: the treating clinician's letter explaining
            medical necessity.
          </li>
          <li>
            For property claims: contractor estimates, weather reports,
            police reports if applicable.
          </li>
        </ul>

        <h2 id="appeal-letter">4. Write the appeal letter</h2>
        <p>A strong appeal letter is short, factual, and specifically addresses the denial reason. A simple structure:</p>
        <ol>
          <li>
            Reference: policy number, claim number, date of denial letter.
          </li>
          <li>
            One sentence stating you are formally appealing the denial.
          </li>
          <li>
            One paragraph quoting the insurer's stated reason.
          </li>
          <li>
            One or two paragraphs explaining why the reason does not apply,
            citing specific policy language and specific evidence.
          </li>
          <li>
            A numbered list of every document you are enclosing.
          </li>
          <li>
            A clear request: "I request that this claim be approved in full
            and processed within the timeframe required under [state]
            insurance regulations."
          </li>
        </ol>
        <p>
          Keep your tone professional. Avoid emotional language — it will not
          help and may be quoted back at you in a regulator complaint.
        </p>

        <h2 id="track">5. Track every piece of correspondence</h2>
        <p>
          From this point forward, every email, letter, voicemail, portal
          message, and phone call needs to be logged. Date, time, who you
          spoke to, what was said, what was promised. Insurers cycle through
          adjusters; what one person told you on a call will not survive a
          handover unless you have it in writing.
        </p>

        <h2 id="organize">Staying organized during your appeal</h2>
        <p>
          The single most common reason appeals fail is not the strength of
          the case — it is that the claimant cannot find the email from
          eight weeks ago that contradicts what the adjuster is now saying.
          A claim file with everything in date order, with key facts
          extracted and commitments tracked, transforms how a regulator or
          ombudsman reads your complaint.
        </p>
        <InlineCta>
          <a href="/welcome">CaseThread</a> turns months of insurer emails
          into a clear timeline, key facts, and a professional dossier you
          can attach to a regulator complaint.
        </InlineCta>

        <h2 id="escalate">6. Escalate to the regulator if needed</h2>
        <p>
          If the internal appeal is denied or the insurer misses its
          response deadline, escalate. In the United States, every state has
          an insurance commissioner who accepts consumer complaints and can
          force insurers to respond. For health insurance specifically, most
          states also offer external review by an independent doctor — the
          insurer is bound by the result.
        </p>
        <p>
          For federally-regulated plans (most employer health insurance),
          the relevant regulator is the U.S. Department of Labor. In the
          UK, the Financial Ombudsman Service handles disputes. In
          Australia, AFCA. Identify the right body before filing — sending a
          complaint to the wrong regulator wastes weeks.
        </p>

        <h2 id="timeline">What to expect on timing</h2>
        <p>
          Most insurers have 30 to 60 days to respond to an internal appeal,
          set by state regulation. Regulator complaints typically resolve in
          90 to 180 days. Keep copies of every confirmation. If a deadline
          slips, that itself is grounds for a complaint.
        </p>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "landlord-security-deposit-dispute",
    title:
      "What to Do When Your Landlord Won't Return Your Security Deposit",
    description:
      "A clear guide to getting your security deposit back: know your rights, document the move-out, send a demand letter, and use small claims court if needed.",
    summary:
      "If your landlord is sitting on your deposit past the legal deadline, you have leverage. Here is how to use it — including the small claims process.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "rights", label: "Know your rights under state law" },
      { id: "document", label: "Document the move-out condition" },
      { id: "demand", label: "Send a written demand letter" },
      { id: "paper-trail", label: "Keeping a paper trail" },
      { id: "small-claims", label: "Filing in small claims court" },
      { id: "deadlines", label: "State-by-state return deadlines" },
      { id: "double-treble", label: "When you can claim multiple damages" },
    ],
    body: () => (
      <>
        <p>
          Security deposits are one of the most regulated areas of landlord
          law, which means tenants who know the rules win the overwhelming
          majority of disputes. The timeline is short, the documentation
          requirements are strict, and many states allow you to recover two
          or three times your deposit if the landlord violates the rules.
        </p>

        <h2 id="rights">Know your rights under state law</h2>
        <p>
          Every U.S. state sets a maximum deadline by which a landlord must
          either return your deposit or send you an itemized list of
          deductions. The deadline is typically between 14 and 60 days after
          you move out. Search your state's name plus "security deposit
          return law" — your state attorney general's website usually has the
          exact statute.
        </p>
        <p>
          Most states require the landlord to send the itemized deductions in
          writing, by mail, to a forwarding address. If the landlord fails
          to do this on time, in many states they forfeit the right to keep
          any of the deposit — even for legitimate damage.
        </p>

        <h2 id="document">Document the move-out condition</h2>
        <p>
          The strongest evidence in a deposit dispute is photographs and
          video taken on move-out day:
        </p>
        <ul>
          <li>
            Photograph every room, every appliance interior, every closet,
            every wall corner. Use a phone with timestamp metadata.
          </li>
          <li>
            Walk through with the landlord if possible and have them sign a
            move-out condition form. If they refuse, send an email
            documenting that you offered.
          </li>
          <li>
            Keep your move-in inspection report and photos. Side-by-side
            comparisons are devastating to inflated deduction claims.
          </li>
          <li>
            Save receipts for any cleaning or repairs you did before
            handing back the keys.
          </li>
        </ul>

        <h2 id="demand">Send a written demand letter</h2>
        <p>
          Once the legal deadline passes, send a formal demand letter by
          certified mail with return receipt. Include:
        </p>
        <ul>
          <li>Your forwarding address (in writing, dated).</li>
          <li>The property address and your move-out date.</li>
          <li>The deposit amount paid.</li>
          <li>
            A reference to the specific state statute the landlord has
            violated.
          </li>
          <li>
            A clear demand: full deposit returned within a stated number of
            days (commonly 7 to 14).
          </li>
          <li>
            A statement that you will file in small claims court for the
            full amount plus any statutory damages if not paid.
          </li>
        </ul>
        <p>
          Certified mail with return receipt matters — it gives you proof the
          letter was received, which a court will accept as service.
        </p>

        <h2 id="paper-trail">Keeping a paper trail</h2>
        <p>
          Texts about repairs, emails about the move-out walkthrough, the
          maintenance request from six months ago that was never addressed —
          all of this matters in court. A judge ruling in a 15-minute small
          claims hearing wants a coherent story with dates, not a folder of
          screenshots in random order.
        </p>
        <InlineCta>
          <a href="/welcome">CaseThread</a> automatically files emails and
          texts into a clear timeline, so the chronology is ready when you
          need it.
        </InlineCta>

        <h2 id="small-claims">Filing in small claims court</h2>
        <p>
          Small claims court exists for exactly this kind of dispute. Filing
          fees are typically $30 to $100. You do not need a lawyer — most
          states ban lawyers from small claims hearings, by design.
        </p>
        <p>The standard process:</p>
        <ol>
          <li>
            File a complaint with the small claims court in the county where
            the property is located.
          </li>
          <li>
            Serve the landlord with the complaint (the court clerk explains
            how — usually by sheriff or certified mail).
          </li>
          <li>
            Show up to the hearing with: lease, deposit receipt, move-in and
            move-out photos, demand letter, certified mail receipt, and any
            correspondence with the landlord.
          </li>
          <li>
            Present a clear chronological story. A one-page printed timeline
            is far more persuasive than thumbing through emails on your
            phone.
          </li>
        </ol>

        <h2 id="deadlines">State-by-state return deadlines</h2>
        <p>
          Examples (verify the current statute for your state — these
          change):
        </p>
        <ul>
          <li>California — 21 days.</li>
          <li>New York — 14 days.</li>
          <li>Texas — 30 days.</li>
          <li>Florida — 15 days (or 30 days with notice).</li>
          <li>Illinois — 30 to 45 days depending on building size.</li>
          <li>Massachusetts — 30 days.</li>
        </ul>

        <h2 id="double-treble">When you can claim multiple damages</h2>
        <p>
          Many states penalize landlords who unlawfully withhold deposits by
          allowing tenants to recover two or even three times the amount
          withheld. Massachusetts allows treble damages plus attorney's fees.
          California allows up to twice the deposit. Check your state's
          statute — if it offers enhanced damages, demand them in your
          complaint.
        </p>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "bank-dispute-resolution",
    title: "How to Resolve a Dispute With Your Bank",
    description:
      "An escalation playbook for bank disputes: from the branch, to the complaints department, to the ombudsman, to the CFPB or OCC.",
    summary:
      "Banks settle the disputes they have to settle. Here is the escalation path that gets results — and the regulators who can force a response when the bank won't.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "branch", label: "Step 1: Start with the branch or call center" },
      {
        id: "complaints-dept",
        label: "Step 2: Escalate to the complaints department",
      },
      { id: "ombudsman", label: "Step 3: Take it to an ombudsman" },
      {
        id: "regulator",
        label: "Step 4: File with the CFPB, OCC, or your state regulator",
      },
      { id: "what-to-include", label: "What to include in a complaint" },
      { id: "organize", label: "Keeping the record straight" },
      { id: "timeline", label: "Realistic timeline expectations" },
    ],
    body: () => (
      <>
        <p>
          Bank disputes follow a predictable escalation ladder. Each rung
          takes longer than the last, but each one significantly increases
          your leverage. Banks track regulator complaints internally — a
          complaint to the CFPB lands on a desk that has authority the
          branch manager does not.
        </p>

        <h2 id="branch">Step 1: Start with the branch or call center</h2>
        <p>
          Almost every bank requires you to give them a chance to resolve
          the issue before regulators will accept a complaint. Make this
          first attempt count:
        </p>
        <ul>
          <li>
            Call or visit. Get the case reference number — banks generate
            one for every dispute and you will need it later.
          </li>
          <li>
            Get the agent's name and ID. Note the date and time.
          </li>
          <li>
            Write down what was said and what was promised. Send yourself a
            confirmation email summarizing the call.
          </li>
          <li>
            If a resolution is offered verbally, ask for it in writing
            before you accept.
          </li>
        </ul>

        <h2 id="complaints-dept">Step 2: Escalate to the complaints department</h2>
        <p>
          If the front-line agent cannot resolve it, ask explicitly to file
          a formal complaint. Every retail bank has a complaints or
          executive-resolution team. Their existence is often not advertised
          — you have to ask for it.
        </p>
        <p>
          The bank is then required to acknowledge the complaint in writing
          (typically within five business days) and provide a final response
          within a regulated window (often 8 weeks in the UK; varies by
          state and product in the US). That final-response letter is
          important — without it, an ombudsman will tell you to come back
          later.
        </p>

        <h2 id="ombudsman">Step 3: Take it to an ombudsman</h2>
        <p>
          If the bank's final response is unsatisfactory, you have free
          access to an independent ombudsman in most jurisdictions:
        </p>
        <ul>
          <li>
            <strong>UK</strong> — Financial Ombudsman Service. Decisions are
            binding on banks if you accept them.
          </li>
          <li>
            <strong>Australia</strong> — Australian Financial Complaints
            Authority (AFCA).
          </li>
          <li>
            <strong>EU</strong> — country-specific ombudsmen, plus FIN-NET
            for cross-border cases.
          </li>
          <li>
            <strong>Canada</strong> — Ombudsman for Banking Services and
            Investments (OBSI).
          </li>
        </ul>

        <h2 id="regulator">Step 4: File with the CFPB, OCC, or your state regulator</h2>
        <p>
          In the United States, the regulator depends on the bank type:
        </p>
        <ul>
          <li>
            <strong>Consumer Financial Protection Bureau (CFPB)</strong> —
            credit cards, mortgages, bank accounts, debt collection,
            student loans, payday loans. Files at consumerfinance.gov.
          </li>
          <li>
            <strong>Office of the Comptroller of the Currency (OCC)</strong>
            {" "}— national banks (those with "National Association" or
            "N.A." in the name).
          </li>
          <li>
            <strong>State banking regulator</strong> — state-chartered
            banks. Each state has one.
          </li>
          <li>
            <strong>Federal Reserve</strong> — state-chartered banks that
            are members of the Federal Reserve System.
          </li>
          <li>
            <strong>FDIC</strong> — state-chartered banks not in the
            Federal Reserve System.
          </li>
        </ul>
        <p>
          If you are unsure, the CFPB has a lookup tool that routes
          complaints to the correct regulator.
        </p>

        <h2 id="what-to-include">What to include in a complaint</h2>
        <p>A complete complaint contains:</p>
        <ol>
          <li>Your account number and full contact information.</li>
          <li>
            A clear chronological narrative — what happened, when, and what
            the bank said at each step.
          </li>
          <li>
            Specific dates and dollar amounts. Vague claims weaken your
            position.
          </li>
          <li>The bank's case reference number.</li>
          <li>
            Copies of every relevant document — statements, emails, letters,
            screenshots of error messages.
          </li>
          <li>The bank's final-response letter, if you have one.</li>
          <li>
            What you want — a refund, a correction to your credit report, a
            written apology, a fee reversal. Be specific.
          </li>
        </ol>

        <h2 id="organize">Keeping the record straight</h2>
        <p>
          Bank disputes drag. By the time you reach the CFPB, you may have
          three months of emails, several call references, two adjusters
          who have moved on, and a contradictory statement from the bank
          you cannot quite locate. Building the timeline as you go — not
          retroactively — is the difference between a five-minute filing
          and a five-hour scramble.
        </p>
        <InlineCta>
          <a href="/welcome">CaseThread</a> consolidates every email, call
          transcript, and screenshot into a single timeline you can attach
          to a CFPB filing.
        </InlineCta>

        <h2 id="timeline">Realistic timeline expectations</h2>
        <p>
          Branch-level resolutions can happen in days. Complaints
          departments take 4 to 8 weeks. Ombudsman cases typically resolve
          in 3 to 6 months. CFPB complaints get a bank response within 15
          days, but actual resolution depends on what the bank decides to
          do — the CFPB does not award damages, it forwards your complaint
          and tracks the response.
        </p>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "organize-dispute-emails",
    title: "How to Organize Emails for a Legal Dispute",
    description:
      "Lawyers and regulators want to see a chronology, not a folder of forwards. Here is how to build a dossier that wins.",
    summary:
      "If you might end up in front of a lawyer, an ombudsman, or a small claims judge — your emails need to be ready. Here is how to do it.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "why", label: "Why email organization matters" },
      { id: "what-they-want", label: "What lawyers and regulators want" },
      { id: "chronology", label: "Building a chronology" },
      { id: "key-facts", label: "Extracting key facts" },
      { id: "dossier", label: "Building a dossier" },
      { id: "automate", label: "Automating the work" },
    ],
    body: () => (
      <>
        <p>
          By the time most disputes reach a lawyer or regulator, the email
          history that proves the case is buried in a Gmail account
          alongside three years of newsletters. The case is winnable, but
          the work to make it winnable is enormous — and most of that work
          is organization, not argument. This guide walks through how
          professionals expect a dispute file to look, and how you can
          build one without spending a weekend in your inbox.
        </p>

        <h2 id="why">Why email organization matters</h2>
        <p>
          A dispute lives or dies on the paper trail. If you can show a
          regulator a clean chronology of what was said and when, your
          credibility is established before they read a single word of your
          argument. If you hand them a folder of 80 forwarded emails with
          no structure, the burden is on them to reconstruct the story —
          and they will not do it.
        </p>
        <p>This is true at every level:</p>
        <ul>
          <li>
            A small claims judge has 15 minutes for your case. A printed
            timeline is read; a phone with 80 emails is not.
          </li>
          <li>
            A regulator's caseworker is processing dozens of complaints
            this week. Your case gets the attention proportional to how
            quickly the relevant facts can be found.
          </li>
          <li>
            A lawyer charges by the hour. Hours spent reconstructing your
            timeline are billed to you.
          </li>
        </ul>

        <h2 id="what-they-want">What lawyers and regulators want</h2>
        <p>
          Across legal disciplines, the dispute file that gets respect has
          the same structure:
        </p>
        <ol>
          <li>
            <strong>A one-page chronology</strong> — date, event, who said
            what. Read in 60 seconds.
          </li>
          <li>
            <strong>A facts sheet</strong> — names, addresses, account
            numbers, reference numbers, dollar amounts. Pulled from the
            correspondence, not retyped.
          </li>
          <li>
            <strong>The full correspondence in date order</strong> — every
            email, letter, portal message, and call transcript, numbered
            and indexed back to the chronology.
          </li>
          <li>
            <strong>The supporting documents</strong> — contracts, receipts,
            photographs, invoices.
          </li>
          <li>
            <strong>A statement of what you want</strong> — refund, repair,
            apology, regulatory action.
          </li>
        </ol>
        <p>
          That is what a "dossier" means in this context. Not a binder of
          everything you have ever received from the other side — a
          curated, indexed, navigable record.
        </p>

        <h2 id="chronology">Building a chronology</h2>
        <p>
          A useful chronology has three columns: date, event, source. The
          source column is critical — every entry should point back to the
          underlying email or document, so anyone reading can verify it
          themselves.
        </p>
        <p>
          The hard part is not writing the entries. The hard part is
          finding every relevant email in the first place. Inbox search is
          unreliable when threads change subject lines, when participants
          drop off, when the same conversation moves from email to a
          customer portal and back. A chronology built from inbox search
          alone is almost always incomplete.
        </p>

        <h2 id="key-facts">Extracting key facts</h2>
        <p>The facts that win cases:</p>
        <ul>
          <li>
            <strong>Commitments</strong> — what did the other side promise,
            and when? "Will refund within 14 days" is gold.
          </li>
          <li>
            <strong>Deadlines</strong> — both deadlines they imposed on
            you, and deadlines they missed.
          </li>
          <li>
            <strong>Contradictions</strong> — what they said in February vs
            what they said in April.
          </li>
          <li>
            <strong>Acknowledgments</strong> — every time they admitted
            fault, even partially.
          </li>
          <li>
            <strong>Reference numbers</strong> — claim, ticket, account,
            policy. Every internal system the other side uses to find your
            file.
          </li>
        </ul>

        <h2 id="dossier">Building a dossier</h2>
        <p>
          Once you have a chronology, key facts, and the underlying
          correspondence, the dossier is a PDF that puts them in order: a
          cover page, the chronology, the facts sheet, then the
          correspondence. Most regulator complaint forms allow attachments
          and a single coherent PDF stands out from the typical flood of
          loose forwards.
        </p>

        <h2 id="automate">Automating the work</h2>
        <p>
          This guide is honest about what the work is — and the work is
          significant. Doing it by hand for a six-month dispute is a full
          weekend. Doing it well is a full weekend plus expertise in what
          to look for.
        </p>
        <p>
          That is what we built CaseThread to do. You forward your dispute
          emails to a unique case address. CaseThread automatically
          extracts dates, parties, commitments, and reference numbers,
          builds the chronology, and exports the dossier as a PDF. What
          takes you a weekend takes the AI a few minutes.
        </p>
        <InlineCta>
          <a href="/welcome">Start a case for free</a> — 30 days, no card
          required.
        </InlineCta>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "file-cfpb-complaint",
    title: "How to File a CFPB Complaint (Step by Step)",
    description:
      "A practical walk-through of filing a CFPB complaint: what they cover, what to include, what happens after, and how to follow up.",
    summary:
      "The Consumer Financial Protection Bureau has more leverage with banks than you do. Here is how to file a complaint that actually gets a response.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "what", label: "What the CFPB covers" },
      { id: "when", label: "When to file" },
      { id: "prepare", label: "Preparing your information" },
      { id: "write", label: "Writing an effective complaint" },
      { id: "after", label: "What happens after you file" },
      { id: "track", label: "Tracking the response" },
      { id: "if-unresolved", label: "If you are not satisfied" },
    ],
    body: () => (
      <>
        <p>
          The Consumer Financial Protection Bureau (CFPB) is the U.S.
          federal agency that takes consumer complaints about financial
          products. Banks respond to CFPB complaints within 15 days, and
          the public CFPB complaints database means there is reputational
          cost to ignoring you. Filing is free, takes about 30 minutes if
          you have your records ready, and is one of the most effective
          consumer levers in the United States.
        </p>

        <h2 id="what">What the CFPB covers</h2>
        <p>The CFPB accepts complaints about:</p>
        <ul>
          <li>Checking and savings accounts.</li>
          <li>Credit cards.</li>
          <li>Mortgages and home equity loans.</li>
          <li>Auto loans and leases.</li>
          <li>Student loans (federal and private).</li>
          <li>Personal loans and payday loans.</li>
          <li>Debt collection (any debt).</li>
          <li>Credit reporting and credit monitoring.</li>
          <li>Money transfers, virtual currency, and prepaid cards.</li>
        </ul>
        <p>
          The CFPB does not handle insurance, securities, or general
          consumer products — those go to your state insurance
          commissioner, the SEC, or the FTC respectively.
        </p>

        <h2 id="when">When to file</h2>
        <p>
          File when the company has had a fair chance to fix the problem
          and has not. There is no formal "you must try internal channels
          first" rule, but the CFPB's process forwards your complaint to
          the company for a response — so if you have not asked them
          directly, the response will simply be "we were never given the
          chance".
        </p>
        <p>Good triggers to file:</p>
        <ul>
          <li>Bank ignored or rejected your formal complaint.</li>
          <li>Bank promised resolution and missed the deadline.</li>
          <li>Errors on your credit report not corrected after a dispute.</li>
          <li>Debt collector contacting you about a debt that is not yours, or that you have already paid.</li>
          <li>Mortgage servicer mishandling escrow, payments, or modifications.</li>
        </ul>

        <h2 id="prepare">Preparing your information</h2>
        <p>Before you start the form, gather:</p>
        <ul>
          <li>Your full name, address, phone, and email.</li>
          <li>The financial company's full legal name.</li>
          <li>Your account number(s).</li>
          <li>Any case reference number the company has given you.</li>
          <li>
            Dates of every relevant interaction — calls, letters, emails,
            payments, fees.
          </li>
          <li>
            Documents you will attach: statements, emails, the company's
            response letter, screenshots, contracts.
          </li>
        </ul>
        <p>
          The CFPB form has a 4,000-character box for your narrative.
          Knowing the chronology in advance keeps you under that limit.
        </p>
        <InlineCta>
          <a href="/welcome">CaseThread</a> assembles the chronology
          automatically from your forwarded emails, so you walk into the
          CFPB form with the dates and amounts ready.
        </InlineCta>

        <h2 id="write">Writing an effective complaint</h2>
        <p>The form asks four key questions. Answer each tightly:</p>
        <ol>
          <li>
            <strong>What happened?</strong> A chronological narrative.
            Lead with the date the problem started. Use specific dates and
            dollar amounts. Avoid emotion — stick to facts.
          </li>
          <li>
            <strong>What company action would resolve your issue?</strong>{" "}
            Be specific. "Refund the $342 overdraft fee charged on March
            14" is actionable. "Treat me fairly" is not.
          </li>
          <li>
            <strong>Has the company already responded?</strong> Yes / no /
            in-progress. If yes, summarize what they said in two sentences.
          </li>
          <li>
            <strong>Account info and dates.</strong> Fill every field —
            blanks slow the routing.
          </li>
        </ol>

        <h2 id="after">What happens after you file</h2>
        <p>The CFPB process:</p>
        <ol>
          <li>
            Within 1 business day, the CFPB forwards your complaint to the
            company.
          </li>
          <li>
            The company has 15 calendar days to provide an initial
            response, and up to 60 days for a final response.
          </li>
          <li>
            You are notified by email when the company responds and can log
            in to view it.
          </li>
          <li>
            You can rate the company's response and provide feedback. This
            feedback is part of the public CFPB database.
          </li>
        </ol>

        <h2 id="track">Tracking the response</h2>
        <p>
          You will get a CFPB complaint ID. Save it. The company's
          response letter will reference it. If they fail to respond, the
          CFPB will mark the complaint as "no response" — that is
          significant and visible to other regulators.
        </p>

        <h2 id="if-unresolved">If you are not satisfied</h2>
        <p>
          The CFPB does not award damages. If the company's response is
          inadequate, your next options:
        </p>
        <ul>
          <li>
            File with your state attorney general's consumer protection
            office.
          </li>
          <li>
            File with the OCC (national banks) or your state banking
            regulator (state banks).
          </li>
          <li>
            For credit reporting issues specifically, sue under the Fair
            Credit Reporting Act in federal court — there is a private
            right of action.
          </li>
          <li>
            For debt collection abuses, sue under the Fair Debt Collection
            Practices Act — also a private right of action with statutory
            damages.
          </li>
          <li>
            Small claims court for amounts under your state's small claims
            limit.
          </li>
        </ul>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "write-formal-complaint-letter",
    title: "How to Write a Formal Complaint Letter (With Template)",
    description:
      "Structure, tone, and a copy-paste template for a complaint letter that gets read, taken seriously, and acted on by the recipient.",
    summary:
      "A complaint letter is the start of every escalation that matters. Here is how to write one that an executive desk, regulator, or court will take seriously.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "why", label: "Why a formal letter matters" },
      { id: "structure", label: "Letter structure" },
      { id: "tone", label: "Setting the right tone" },
      { id: "include", label: "What to include" },
      { id: "avoid", label: "What to avoid" },
      { id: "send", label: "How to send it" },
      { id: "follow-up", label: "Following up" },
      { id: "template", label: "Template outline" },
    ],
    body: () => (
      <>
        <p>
          A complaint letter is not a vent. It is a document that may be
          read by a customer-service agent today, an executive next week,
          and a regulator or judge six months from now. The same letter has
          to do all three jobs. The good news is that a structure exists,
          it is not complicated, and using it dramatically increases the
          chance you get a useful response.
        </p>

        <h2 id="why">Why a formal letter matters</h2>
        <p>
          Phone calls disappear. Chat sessions time out. A formal letter is
          a paper anchor — the recipient cannot pretend it was not received,
          cannot remember it differently next month, and cannot quietly
          drop it from a customer record. Most companies route formal
          letters to a different (slower, but more powerful) team than
          casual contact, and most regulators will not act on your complaint
          until you have written one and given the company a chance to
          respond.
        </p>

        <h2 id="structure">Letter structure</h2>
        <p>A workable five-part structure:</p>
        <ol>
          <li>
            <strong>Header</strong> — your name and address, the date, the
            recipient's name and address.
          </li>
          <li>
            <strong>Subject line</strong> — clear and reference-rich.
            "Formal complaint — account 12345 — billing dispute".
          </li>
          <li>
            <strong>Facts</strong> — one paragraph stating what happened, in
            chronological order, with dates and amounts.
          </li>
          <li>
            <strong>What you want</strong> — one paragraph stating exactly
            what would resolve the issue. Be specific.
          </li>
          <li>
            <strong>Deadline and consequences</strong> — a stated deadline
            and what you will do next if it is missed (escalate to ombudsman,
            file with regulator, take to small claims).
          </li>
        </ol>

        <h2 id="tone">Setting the right tone</h2>
        <p>
          Professional, factual, and slightly formal. The reader should not
          be able to tell whether you are writing in frustration or with
          mild detachment. Why? Because the letter may be quoted back at
          you in a regulator filing or court case. An angry tone gives the
          other side an easy out: "the customer was abusive". A clinical
          tone leaves them with nothing to attack.
        </p>

        <h2 id="include">What to include</h2>
        <ul>
          <li>
            <strong>Account, policy, or reference numbers</strong> — every
            number that identifies your file.
          </li>
          <li>
            <strong>Specific dates</strong> — when the problem started, when
            you first reported it, every contact since.
          </li>
          <li>
            <strong>Names</strong> — who you spoke to and on what date.
          </li>
          <li>
            <strong>Amounts</strong> — exact dollar figures, not "a few
            hundred".
          </li>
          <li>
            <strong>What was promised</strong> — quotes from emails or
            commitments made on calls.
          </li>
          <li>
            <strong>Enclosures list</strong> — every document you are
            attaching, numbered.
          </li>
          <li>
            <strong>The specific resolution you want</strong> — a refund of
            $X, a correction to your credit report, a written apology, a
            reissued policy.
          </li>
        </ul>

        <h2 id="avoid">What to avoid</h2>
        <ul>
          <li>
            <strong>Threats you cannot or will not follow through on.</strong>
            {" "}If you say you will sue, mean it.
          </li>
          <li>
            <strong>Personal attacks on individual employees.</strong> Stick
            to the company's actions.
          </li>
          <li>
            <strong>Long backstory.</strong> The reader does not need
            paragraphs of context. Lead with the facts that matter.
          </li>
          <li>
            <strong>Legal jargon you do not understand.</strong> Sprinkling
            "estoppel" and "consideration" into a consumer complaint just
            makes the recipient roll their eyes.
          </li>
          <li>
            <strong>Multiple complaints in one letter.</strong> One issue,
            one letter. Otherwise the response addresses one complaint and
            ignores the rest.
          </li>
        </ul>
        <InlineCta>
          <a href="/welcome">CaseThread</a> assembles the dates, names, and
          commitments from your forwarded emails, so the facts paragraph
          almost writes itself.
        </InlineCta>

        <h2 id="send">How to send it</h2>
        <p>
          Send by certified mail with return receipt requested. The receipt
          is your proof of delivery and the date you can use as the start
          of any deadline. Email a copy as well — most companies have a
          dedicated complaints address (often complaints@ or
          executiveoffice@). Keep a copy of everything.
        </p>

        <h2 id="follow-up">Following up</h2>
        <p>
          Most consumer regulators expect the recipient to respond within
          14 to 30 days. If your stated deadline passes, send a brief
          follow-up letter referencing the original, then escalate. Do not
          repeat the original arguments — escalation is its own letter, to
          a different audience (regulator, ombudsman, court).
        </p>

        <h2 id="template">Template outline</h2>
        <p>Copy-paste skeleton:</p>
        <ul>
          <li>[Your name + address]</li>
          <li>[Date]</li>
          <li>[Recipient name, title, company, address]</li>
          <li>
            <strong>Re:</strong> Formal complaint — [account/reference] —
            [one-line subject]
          </li>
          <li>Dear [recipient],</li>
          <li>
            I am writing to formally complain about [issue]. The relevant
            account is [number]. The chronology is as follows: [date 1 —
            event], [date 2 — event], [date 3 — event].
          </li>
          <li>
            On [date], your representative [name] confirmed [commitment].
            That commitment has not been honoured.
          </li>
          <li>
            I request that [specific resolution]. I expect a substantive
            response within [number] days of this letter.
          </li>
          <li>
            If I do not receive a satisfactory response by [date], I will
            escalate to [regulator / ombudsman / small claims court].
          </li>
          <li>
            Enclosed: [numbered list of documents].
          </li>
          <li>Yours sincerely, [your name].</li>
        </ul>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "track-correspondence-for-legal-case",
    title: "How to Track Correspondence for a Legal Case",
    description:
      "Why chronology wins cases, what your lawyer actually wants, and how to build an evidence binder that pays for itself in billable hours saved.",
    summary:
      "Lawyers do not want a folder of forwarded emails. They want a chronology, a facts sheet, and an indexed binder. Here is how to build one.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "why", label: "Why chronology wins cases" },
      { id: "lawyer-wants", label: "What your lawyer actually wants" },
      { id: "by-date", label: "Organize by date, not by topic" },
      { id: "binder", label: "Building an evidence binder" },
      { id: "automate", label: "Letting tools do the heavy lifting" },
      { id: "maintain", label: "Maintaining the file as the case progresses" },
    ],
    body: () => (
      <>
        <p>
          Legal cases are won by the side with the better paper trail. Not
          the cleverer argument, not the more sympathetic story — the side
          who can produce the email from eight months ago that contradicts
          the other party's current position. That advantage exists for
          anyone willing to track their correspondence properly from day
          one. This guide is about how.
        </p>

        <h2 id="why">Why chronology wins cases</h2>
        <p>
          A judge, mediator, or arbitrator does not have time to read
          fifty emails to figure out what happened. They want a one-page
          chronology. If you can hand it to them and every entry can be
          traced to a specific document, your version of events becomes
          the default version. The other side now has to explain why the
          chronology is wrong — a much harder position than starting from
          zero.
        </p>
        <p>
          Beyond credibility, a clean chronology often surfaces patterns
          the parties themselves have not noticed. "They promised X on
          three separate dates" or "every commitment they made in writing
          was missed by exactly two weeks" are the kinds of observations
          that turn into winning arguments.
        </p>

        <h2 id="lawyer-wants">What your lawyer actually wants</h2>
        <p>
          Your lawyer does not want every email you have ever sent. They
          want, in this order:
        </p>
        <ol>
          <li>
            <strong>A one-page chronology</strong> — date, event, source.
          </li>
          <li>
            <strong>A facts sheet</strong> — names, addresses, account
            numbers, reference numbers, dollar amounts.
          </li>
          <li>
            <strong>The full correspondence in date order</strong> — emails,
            letters, portal messages, call notes — numbered and indexed
            back to the chronology.
          </li>
          <li>
            <strong>Supporting documents</strong> — contracts, receipts,
            photographs, expert reports.
          </li>
          <li>
            <strong>What you want</strong> — your stated position on the
            outcome.
          </li>
        </ol>
        <p>
          Hand this to your lawyer at the first meeting and the conversation
          jumps three sessions ahead. Hand them a Gmail folder and you pay
          for those three sessions.
        </p>

        <h2 id="by-date">Organize by date, not by topic</h2>
        <p>
          A common mistake is to file by topic — "billing", "appeals",
          "warranty". This feels organized but obscures the chronology that
          matters in court. The right structure is strictly chronological.
          Topic groupings can sit on top as cross-references, but the
          underlying file is a timeline.
        </p>
        <p>
          When a topic groups matters (e.g., everything related to a
          specific incident), tag entries in the chronology rather than
          breaking them into separate folders. The chronology is the
          backbone; tags are the ribs.
        </p>

        <h2 id="binder">Building an evidence binder</h2>
        <p>The traditional structure, still used by most law firms:</p>
        <ul>
          <li>
            <strong>Tab 1 — chronology.</strong> One page, three columns
            (date, event, document number).
          </li>
          <li>
            <strong>Tab 2 — facts sheet.</strong> Names, addresses,
            references, amounts.
          </li>
          <li>
            <strong>Tab 3 — contracts and policies.</strong> The governing
            documents.
          </li>
          <li>
            <strong>Tabs 4 onwards — correspondence in date order.</strong>
            {" "}One document per tab divider, numbered to match the
            chronology.
          </li>
          <li>
            <strong>Final tab — supporting evidence.</strong> Receipts,
            photographs, expert reports.
          </li>
        </ul>
        <p>
          Digital binders work the same way: a single PDF with bookmarks,
          or a folder with numbered files. Hand a working PDF to a regulator
          and you stand out from the background noise of unstructured
          complaints.
        </p>

        <h2 id="automate">Letting tools do the heavy lifting</h2>
        <p>
          Building this manually for a six-month dispute is a full weekend
          of work, plus ongoing maintenance every time a new email arrives.
          Most people start with good intentions and abandon the system
          after a month, ending up exactly where they started: a Gmail
          folder full of forwards.
        </p>
        <p>
          That is the gap CaseThread fills. You forward correspondence to a
          unique case email address. The chronology builds itself. Key
          facts (parties, dates, amounts, commitments) are extracted
          automatically. The dossier exports as a PDF in the structure your
          lawyer or regulator expects.
        </p>
        <InlineCta>
          <a href="/welcome">Try CaseThread free for 30 days</a> — no card
          required. Forward your first email and see the chronology appear.
        </InlineCta>

        <h2 id="maintain">Maintaining the file as the case progresses</h2>
        <p>
          A file that was perfectly organized in February is useless if it
          stops being maintained in March. Whatever system you choose, the
          rule is: every new piece of correspondence goes into the file the
          same day it arrives. Five minutes a week is enough; a month of
          backlog is the point at which most people give up.
        </p>
        <p>
          Re-export the dossier at every milestone — when you send a formal
          complaint, when you escalate to a regulator, when your lawyer
          sends a demand letter. Each export is a snapshot you can hand to
          a new participant without rebuilding from scratch.
        </p>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "small-claims-court-preparation",
    title: "How to Prepare for Small Claims Court",
    description:
      "What to bring, how to organize evidence, and how to present a case to a small claims judge in the 15 minutes you will get.",
    summary:
      "Small claims is fast — most hearings are under 20 minutes. Preparation is everything. Here is what judges actually want to see.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "what", label: "What small claims handles" },
      { id: "documents", label: "Documents to bring" },
      { id: "organize", label: "Organizing your evidence" },
      { id: "timeline", label: "Presenting a timeline to the judge" },
      { id: "judges", label: "What judges actually look for" },
      { id: "mistakes", label: "Common mistakes" },
      { id: "day-of", label: "The day of the hearing" },
    ],
    body: () => (
      <>
        <p>
          Small claims court is designed for ordinary people to resolve
          ordinary disputes without lawyers. The procedure is informal, the
          fees are low, and the judge expects to spend less than 20
          minutes on your case. That brevity makes preparation more
          important than in any other forum — you do not get a chance to
          slowly build a case in front of a judge who is already running
          late.
        </p>

        <h2 id="what">What small claims handles</h2>
        <p>
          Money disputes up to a state-set limit, typically between $5,000
          and $25,000. Common cases:
        </p>
        <ul>
          <li>Unreturned security deposits.</li>
          <li>Unpaid invoices for goods or services rendered.</li>
          <li>Property damage caused by another party.</li>
          <li>Refunds for defective products or undelivered services.</li>
          <li>Minor breach of contract claims.</li>
        </ul>
        <p>
          Most states bar lawyers from small claims hearings, so do not
          worry about being outgunned. Both sides are on the same footing.
        </p>

        <h2 id="documents">Documents to bring</h2>
        <p>Bring two of everything — one for you, one for the judge:</p>
        <ul>
          <li>The original contract or written agreement, if any.</li>
          <li>
            All correspondence — emails, letters, texts, portal messages.
            Print them. The judge cannot search your phone.
          </li>
          <li>
            Receipts, invoices, payment records, statements showing what was
            charged or paid.
          </li>
          <li>
            Photographs or video evidence (printed where possible).
          </li>
          <li>
            Repair estimates or expert reports.
          </li>
          <li>
            A demand letter you sent before filing, with the certified mail
            receipt.
          </li>
          <li>
            A one-page chronology summarizing the case.
          </li>
          <li>
            Witness statements (signed, if witnesses are not appearing).
          </li>
        </ul>

        <h2 id="organize">Organizing your evidence</h2>
        <p>
          Use a binder with tabs or a single PDF with bookmarks. Number
          every document. The chronology should reference each document by
          number so the judge can flip directly to the source.
        </p>
        <p>
          The structure that wins:
        </p>
        <ol>
          <li>One-page chronology (top of the binder).</li>
          <li>Underlying contract.</li>
          <li>Correspondence in date order, numbered.</li>
          <li>Supporting evidence (photos, receipts, estimates).</li>
          <li>Demand letter and proof of service.</li>
        </ol>
        <InlineCta>
          <a href="/welcome">CaseThread</a> exports exactly this structure
          as a single PDF dossier — chronology first, then correspondence
          in date order — ready to print for the judge.
        </InlineCta>

        <h2 id="timeline">Presenting a timeline to the judge</h2>
        <p>
          When the judge asks you to explain the case, do not start with
          background or your feelings about the defendant. Start with the
          chronology:
        </p>
        <p>
          "Your honor, this is a security deposit case. On March 1 I moved
          out and provided my forwarding address. Under [state] law, the
          landlord had 21 days to return the deposit or send an itemized
          statement. The deadline passed on March 22. I sent a demand
          letter on March 28. I filed in this court on April 15. The
          deposit has not been returned."
        </p>
        <p>
          That is your case in 60 seconds. Now hand the judge the binder
          and stop talking. Most judges ask follow-up questions; let them
          drive.
        </p>

        <h2 id="judges">What judges actually look for</h2>
        <ul>
          <li>
            <strong>A clear timeline.</strong> If they cannot follow what
            happened in what order, they cannot rule for you.
          </li>
          <li>
            <strong>Documentary proof of every claim.</strong> "He said he
            would" loses to "here is the email where he said he would".
          </li>
          <li>
            <strong>Specific dollar amounts.</strong> Vague damages claims
            get reduced or dismissed.
          </li>
          <li>
            <strong>Evidence you tried to resolve it first.</strong> Most
            jurisdictions expect a demand letter before a lawsuit.
          </li>
          <li>
            <strong>Calm presentation.</strong> Emotional argument loses
            credibility fast in a small claims setting.
          </li>
        </ul>

        <h2 id="mistakes">Common mistakes</h2>
        <ul>
          <li>
            <strong>Bringing only your phone.</strong> Print everything.
          </li>
          <li>
            <strong>Not bringing the contract.</strong> The judge will ask
            for it. If you cannot produce it, the case may be continued or
            dismissed.
          </li>
          <li>
            <strong>Suing the wrong party.</strong> Sue the legal entity on
            the contract, not the individual employee.
          </li>
          <li>
            <strong>Filing in the wrong county.</strong> Usually the county
            where the defendant lives or where the contract was performed.
          </li>
          <li>
            <strong>Not serving the defendant properly.</strong> The court
            clerk explains the rules — follow them exactly or your case
            will be dismissed at the hearing.
          </li>
          <li>
            <strong>Asking for too much.</strong> Inflate damages and the
            judge tunes out. Stick to provable, documented amounts.
          </li>
        </ul>

        <h2 id="day-of">The day of the hearing</h2>
        <p>
          Arrive 30 minutes early. Dress as you would for a job interview.
          Watch a few cases before yours so you know the judge's style. When
          your case is called, stand, state your name, and let the judge
          set the pace. Address the judge as "your honor". Answer
          questions directly without interrupting.
        </p>
        <p>
          If you win, the judgment is enforceable but not self-executing —
          you may still need to take additional steps to actually collect.
          The court clerk will explain.
        </p>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "dispute-medical-bill",
    title: "How to Dispute a Medical Bill",
    description:
      "Review the bill, request an itemization, find the errors, negotiate, appeal denials, and apply for financial assistance — step by step.",
    summary:
      "Up to 80% of medical bills contain errors. Here is how to find them, dispute the charges, and use the financial assistance programs hospitals would rather you not know about.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "review", label: "Review the bill carefully" },
      { id: "itemize", label: "Request an itemized statement" },
      { id: "errors", label: "Check for billing errors" },
      { id: "negotiate", label: "Negotiate the amount" },
      { id: "appeal", label: "Appeal denied insurance coverage" },
      { id: "assistance", label: "Financial assistance programs" },
      { id: "track", label: "Keep records of every conversation" },
    ],
    body: () => (
      <>
        <p>
          Medical billing in the United States is a system that produces
          errors at staggering rates. Industry studies suggest 30 to 80
          percent of hospital bills contain mistakes, and most patients pay
          them without checking. The fix is largely procedural — request
          the right document, check it line by line, and use the formal
          channels that exist for exactly this purpose.
        </p>

        <h2 id="review">Review the bill carefully</h2>
        <p>
          The summary statement most patients receive is not a real bill —
          it is a marketing document. It tells you the total but leaves out
          everything you need to dispute it. Before paying anything, get
          the underlying detail.
        </p>
        <p>
          Confirm the basics first: your name, your date of service, your
          insurance information, the provider's name. Errors in any of
          these often mean the claim was processed under the wrong patient
          or the wrong policy.
        </p>

        <h2 id="itemize">Request an itemized statement</h2>
        <p>
          Call the billing department and request a fully itemized
          statement listing every CPT code, every supply, every medication.
          You are entitled to this — under federal law most providers must
          supply it on request. Get the request in writing as well (email
          or portal message), so you have a record.
        </p>
        <p>
          When the itemization arrives, look for:
        </p>
        <ul>
          <li>
            <strong>Charges for services you did not receive.</strong> Tests
            cancelled but billed, medications never administered.
          </li>
          <li>
            <strong>Duplicate charges.</strong> Same code billed twice.
          </li>
          <li>
            <strong>Unbundled charges.</strong> A single procedure broken
            into separate billable codes that should be one.
          </li>
          <li>
            <strong>Upcoded charges.</strong> A more expensive code used
            than the service warranted.
          </li>
          <li>
            <strong>Charges past the expected length of stay.</strong>{" "}
            Hospital room billed for a day you were already discharged.
          </li>
        </ul>

        <h2 id="errors">Check for billing errors</h2>
        <p>
          Cross-reference the itemization against your medical records and
          your insurance Explanation of Benefits (EOB). The EOB shows what
          was charged, what insurance allowed, what insurance paid, and
          what you owe. The hospital bill should match the EOB's "patient
          responsibility" amount. If it does not, that is a dispute.
        </p>
        <p>
          Common discrepancies:
        </p>
        <ul>
          <li>
            Hospital billing you for an amount higher than what insurance
            says you owe.
          </li>
          <li>
            Out-of-network charges from a provider you reasonably believed
            was in-network (the No Surprises Act covers many of these).
          </li>
          <li>
            Coding errors that flagged a covered service as non-covered.
          </li>
        </ul>

        <h2 id="negotiate">Negotiate the amount</h2>
        <p>
          Hospitals routinely settle bills for 30 to 60 percent of the
          billed amount. The leverage:
        </p>
        <ul>
          <li>
            <strong>Cash discount.</strong> Offer to pay a reduced amount
            today in exchange for closing the bill. Many billing
            departments are authorized to accept 50 to 70 percent.
          </li>
          <li>
            <strong>Charity-rate adjustment.</strong> Most non-profit
            hospitals are required to offer charity care at defined income
            levels. Even if you do not qualify, the conversation often
            opens up other discounts.
          </li>
          <li>
            <strong>Prompt-pay discount.</strong> 10 to 20 percent off for
            paying within a stated window.
          </li>
        </ul>
        <p>
          Get any negotiated rate in writing before you pay. Verbal deals
          on a recorded billing call still need a written confirmation.
        </p>
        <InlineCta>
          <a href="/welcome">CaseThread</a> keeps every billing call,
          email, and EOB together with extracted dates and dollar amounts —
          the documentation a successful appeal depends on.
        </InlineCta>

        <h2 id="appeal">Appeal denied insurance coverage</h2>
        <p>
          If insurance denied a claim that should have been covered, file
          a formal appeal:
        </p>
        <ol>
          <li>
            Read the denial reason on the EOB and the relevant section of
            your policy document.
          </li>
          <li>
            Get a letter from your treating clinician explaining medical
            necessity.
          </li>
          <li>
            Submit a written appeal within the deadline stated on the EOB
            (often 180 days).
          </li>
          <li>
            If the internal appeal is denied, request external review by an
            independent reviewer. In most states this is free and binding
            on the insurer.
          </li>
        </ol>

        <h2 id="assistance">Financial assistance programs</h2>
        <p>
          Most non-profit hospitals are required by federal law to offer
          financial assistance to patients below set income thresholds.
          The application is rarely advertised. Ask for the financial
          assistance application. Income up to 200 to 400 percent of the
          federal poverty line often qualifies for partial or full
          forgiveness.
        </p>
        <p>
          For-profit hospitals also have hardship programs, though the
          terms vary. Always ask. The worst they can say is no.
        </p>

        <h2 id="track">Keep records of every conversation</h2>
        <p>
          Medical billing disputes drag on for months and cycle through
          multiple departments. Track every call (date, time, who you spoke
          to, what was said, what was promised). Save every email and EOB.
          When you escalate to your state insurance commissioner or the
          hospital's patient advocate, the documentation you assembled is
          the difference between a five-minute resolution and a five-month
          impasse.
        </p>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "car-insurance-claim-denied",
    title: "What to Do When Your Car Insurance Claim Is Denied",
    description:
      "Why car insurance claims get denied, how to appeal, when to involve the state insurance commissioner, and how bad faith claims work.",
    summary:
      "Your car insurer said no. Most denials are reversible if you appeal correctly. Here is how — and when to call in the regulator.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "reasons", label: "Common reasons claims are denied" },
      { id: "read-letter", label: "Read the denial letter carefully" },
      { id: "evidence", label: "Gather supporting evidence" },
      { id: "appeal", label: "File the internal appeal" },
      { id: "commissioner", label: "Escalate to the state insurance commissioner" },
      { id: "bad-faith", label: "Bad faith insurance claims" },
      { id: "document", label: "Documenting every step" },
    ],
    body: () => (
      <>
        <p>
          Car insurance claim denials follow predictable patterns. The
          insurer cites a policy clause; you have to show why it does not
          apply. Most denials are reversed on internal appeal or after a
          state insurance commissioner gets involved. The cases that fail
          are usually the ones where the policyholder gives up after the
          first "no".
        </p>

        <h2 id="reasons">Common reasons claims are denied</h2>
        <ul>
          <li>
            <strong>Lapsed coverage.</strong> Premium not paid on the date
            of loss. Sometimes the insurer is wrong about when the policy
            lapsed.
          </li>
          <li>
            <strong>Excluded driver.</strong> The vehicle was driven by
            someone not listed on the policy.
          </li>
          <li>
            <strong>Pre-existing damage.</strong> The insurer claims the
            damage was there before the incident.
          </li>
          <li>
            <strong>Fraud allegation.</strong> The insurer suspects the
            claim is exaggerated or staged.
          </li>
          <li>
            <strong>Policy exclusions.</strong> Comprehensive vs collision
            coverage gaps, racing exclusions, business-use exclusions.
          </li>
          <li>
            <strong>Late notice.</strong> The claim was reported after a
            policy deadline.
          </li>
          <li>
            <strong>Insufficient documentation.</strong> Photos, police
            reports, or repair estimates missing.
          </li>
        </ul>

        <h2 id="read-letter">Read the denial letter carefully</h2>
        <p>
          The denial letter is required to state the specific reason and
          the specific policy provision. Note:
        </p>
        <ul>
          <li>The exact policy clause cited (verify it against your policy).</li>
          <li>The deadline to appeal (typically 30 to 60 days).</li>
          <li>The specific facts the adjuster relied on.</li>
          <li>The address or portal for submitting the appeal.</li>
        </ul>
        <p>
          If the denial is vague, write back demanding the specific reason
          and specific policy citation. Insurers cannot legally deny a
          claim without grounds.
        </p>

        <h2 id="evidence">Gather supporting evidence</h2>
        <ul>
          <li>The full policy document and any endorsements.</li>
          <li>The police report (request from the responding agency).</li>
          <li>
            Photos of the vehicle, the scene, and any other vehicles
            involved.
          </li>
          <li>Witness statements (with names and contact information).</li>
          <li>
            Repair estimates from independent shops, not just the insurer's
            preferred network.
          </li>
          <li>Medical records if injury claims are involved.</li>
          <li>
            Every email, letter, portal message, and call log with the
            insurer.
          </li>
        </ul>

        <h2 id="appeal">File the internal appeal</h2>
        <p>
          Submit a formal written appeal addressing each reason for
          denial. Quote the insurer's stated reason. Quote the policy
          language. Cite the evidence that contradicts the denial. End
          with a clear request: that the claim be approved and processed
          within the timeframe required by your state's insurance
          regulations.
        </p>
        <p>
          Send by certified mail with return receipt. Most state laws
          require the insurer to respond within 30 days.
        </p>
        <InlineCta>
          <a href="/welcome">CaseThread</a> consolidates the police report,
          photos, repair estimates, and every back-and-forth into one
          dossier — exactly what an appeal letter relies on.
        </InlineCta>

        <h2 id="commissioner">Escalate to the state insurance commissioner</h2>
        <p>
          If the appeal is denied or ignored, file a complaint with your
          state insurance commissioner. Every state has one. The complaint
          process is free, the form is short, and insurers have to respond
          to commissioner complaints within a regulated timeframe (usually
          15 to 30 days).
        </p>
        <p>
          Include in the complaint:
        </p>
        <ul>
          <li>Your policy number and claim number.</li>
          <li>A chronology of the claim and denial.</li>
          <li>The denial letter and your appeal letter.</li>
          <li>What you want the commissioner to investigate.</li>
        </ul>
        <p>
          Commissioner complaints alone change outcomes. Insurers track
          complaint counts internally and adjusters know that complaints
          require management explanations.
        </p>

        <h2 id="bad-faith">Bad faith insurance claims</h2>
        <p>
          Insurers have a legal duty to deal with claims in good faith.
          When they fail — by misrepresenting policy terms, by failing to
          investigate, by denying valid claims without reasonable basis,
          by lowballing settlement offers — the policyholder may have a
          bad faith claim that is separate from (and often worth more
          than) the original insurance claim.
        </p>
        <p>
          Indicators of bad faith:
        </p>
        <ul>
          <li>Repeated unexplained delays.</li>
          <li>Demanding documentation that has already been provided.</li>
          <li>Misquoting the policy.</li>
          <li>Pressure tactics to accept a low settlement.</li>
          <li>Failure to respond within statutory timeframes.</li>
        </ul>
        <p>
          Bad faith claims are typically handled by attorneys on
          contingency — they take a percentage of any recovery rather than
          charging up front. Worth a free consultation if the pattern fits.
        </p>

        <h2 id="document">Documenting every step</h2>
        <p>
          Insurers cycle adjusters frequently. The agent who promised you
          something in February may be gone in April. Every promise needs
          to be in writing — a confirmation email after a phone call is
          enough. Without that paper trail, an appeal is your word against
          the file note the insurer chose to write.
        </p>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "warranty-claim-refused",
    title: "What to Do When a Company Refuses Your Warranty Claim",
    description:
      "Know your warranty terms, the federal consumer protections, the escalation path, and how to file with the FTC and state AG.",
    summary:
      "Warranty refusals often fold once consumer protection law is on the table. Here is the ladder — from terms review to FTC complaint to small claims.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "terms", label: "Know your warranty terms" },
      { id: "magnuson", label: "Federal protection: Magnuson-Moss" },
      { id: "implied", label: "State implied warranties" },
      { id: "escalate", label: "The escalation path" },
      { id: "ftc-ag", label: "File with the FTC and state attorney general" },
      { id: "document", label: "Documenting the timeline" },
      { id: "small-claims", label: "When to consider small claims" },
    ],
    body: () => (
      <>
        <p>
          A refused warranty claim is rarely the end of the story.
          Companies refuse warranty claims as a matter of routine, knowing
          most customers will go away. The customers who do not go away —
          who cite the law, escalate, and file complaints — get a
          remarkably high resolution rate. This guide walks the ladder
          from polite request to formal complaint.
        </p>

        <h2 id="terms">Know your warranty terms</h2>
        <p>
          Pull the actual warranty document — the paper one that came with
          the product, the PDF on the manufacturer's website, or the terms
          published at point of sale. Read what is covered, for how long,
          and what the claim procedure is.
        </p>
        <p>Pay attention to:</p>
        <ul>
          <li>Coverage period (often longer than the seller suggests).</li>
          <li>What is covered (parts, labor, replacement).</li>
          <li>Exclusions (misuse, modification, normal wear).</li>
          <li>The required claim procedure (forms, return shipping, proof of purchase).</li>
          <li>Whether the warranty is "full" or "limited" — terms of art under federal law.</li>
        </ul>

        <h2 id="magnuson">Federal protection: Magnuson-Moss</h2>
        <p>
          The Magnuson-Moss Warranty Act is the federal law governing
          consumer product warranties. Key protections:
        </p>
        <ul>
          <li>
            Companies that offer written warranties must honor them, and
            cannot disclaim implied warranties on top.
          </li>
          <li>
            Warranties must be clear, conspicuous, and available for
            inspection before purchase.
          </li>
          <li>
            "Tie-in" requirements (must use brand-name parts, must service
            at brand dealers) are generally unlawful unless the company
            provides the parts or service for free.
          </li>
          <li>
            Successful plaintiffs in a Magnuson-Moss case can recover
            attorney's fees and costs from the seller. This makes
            attorneys willing to take small consumer cases on contingency.
          </li>
        </ul>

        <h2 id="implied">State implied warranties</h2>
        <p>
          Even without a written warranty, every U.S. state recognizes the
          implied warranty of merchantability — that goods sold are fit for
          their ordinary purpose. A toaster has to toast. A washing machine
          has to wash.
        </p>
        <p>
          Many states also recognize the implied warranty of fitness for a
          particular purpose, which applies when the seller knew you needed
          the product for a specific use and recommended it for that use.
        </p>
        <p>
          These implied warranties typically apply for a state-defined
          period — often four years from purchase under the Uniform
          Commercial Code — regardless of what the express written warranty
          says.
        </p>

        <h2 id="escalate">The escalation path</h2>
        <ol>
          <li>
            <strong>Customer service.</strong> File the claim through the
            stated channel. Get a case number. Document every interaction.
          </li>
          <li>
            <strong>Supervisor or warranty escalation team.</strong> Most
            companies have a tier-2 team. Ask for it explicitly.
          </li>
          <li>
            <strong>Written formal complaint.</strong> Send a certified
            letter to the corporate complaints address citing the warranty
            terms, Magnuson-Moss, and your state's implied warranty law.
          </li>
          <li>
            <strong>Manufacturer if you bought from a retailer.</strong>{" "}
            The manufacturer often has a separate (and more responsive)
            warranty channel than the retailer.
          </li>
          <li>
            <strong>Regulatory complaint.</strong> See the next section.
          </li>
        </ol>
        <InlineCta>
          <a href="/welcome">CaseThread</a> tracks every email, call, and
          shipping receipt across the warranty timeline — so the formal
          complaint letter writes itself with accurate dates.
        </InlineCta>

        <h2 id="ftc-ag">File with the FTC and state attorney general</h2>
        <p>
          The Federal Trade Commission accepts consumer complaints about
          warranty violations through reportfraud.ftc.gov. The FTC does
          not resolve individual disputes but uses complaint patterns to
          drive enforcement actions, and a complaint on file matters in
          any subsequent escalation.
        </p>
        <p>
          Your state attorney general's consumer protection office is
          often more useful for individual cases. State AGs can and do
          contact companies directly on consumer complaints, and many
          companies treat AG inquiries as priority resolutions.
        </p>
        <p>
          For specific product categories, additional regulators apply:
        </p>
        <ul>
          <li>Cars — National Highway Traffic Safety Administration (NHTSA).</li>
          <li>Appliances and electronics — Consumer Product Safety Commission (for safety issues).</li>
          <li>Tools and machinery — state weights and measures authority.</li>
        </ul>

        <h2 id="document">Documenting the timeline</h2>
        <p>
          Warranty claims often hinge on dates: when you bought the
          product, when the defect appeared, when you reported it, when
          the company responded, when they shipped or refused to ship the
          repair. A clean timeline strips the company's "we never heard
          back from you" defense before they raise it.
        </p>

        <h2 id="small-claims">When to consider small claims</h2>
        <p>
          If the dollar amount is under your state's small claims limit
          and the company will not engage, small claims court is the
          natural endgame. Magnuson-Moss claims can be brought in small
          claims, and a judgment is enforceable. Even the threat of small
          claims often produces a settlement — the cost of defending a
          $300 claim across the country is more than the claim itself.
        </p>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "respond-to-debt-collector",
    title: "How to Respond to a Debt Collector (Know Your Rights)",
    description:
      "Your FDCPA rights, how to demand verification, dispute the debt, document every contact, and send a cease-and-desist letter.",
    summary:
      "Debt collectors operate within strict legal limits. Knowing those limits — and using them — turns a stressful call into a manageable process.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "rights", label: "Your rights under the FDCPA" },
      { id: "verification", label: "Send a debt verification letter" },
      { id: "dispute", label: "Dispute the debt in writing" },
      { id: "document", label: "Document every contact" },
      { id: "cease", label: "Cease and desist letters" },
      { id: "violations", label: "Reporting violations" },
      { id: "valid", label: "If the debt is yours" },
    ],
    body: () => (
      <>
        <p>
          The first call from a debt collector is meant to feel
          overwhelming. The script is designed to make you commit to a
          payment immediately, before you can think. Federal law gives you
          significantly more protection and time than the call suggests,
          and using those protections is straightforward once you know
          they exist.
        </p>

        <h2 id="rights">Your rights under the FDCPA</h2>
        <p>
          The federal Fair Debt Collection Practices Act covers every
          third-party collector contacting U.S. consumers about personal
          debt. Your core rights:
        </p>
        <ul>
          <li>
            <strong>You can require all communication in writing.</strong>{" "}
            Once you make this request, calls must stop.
          </li>
          <li>
            <strong>Collectors cannot call before 8 AM or after 9 PM.</strong>
          </li>
          <li>
            <strong>Collectors cannot contact you at work</strong> if you
            have told them not to (verbally or in writing).
          </li>
          <li>
            <strong>Collectors cannot contact friends, family, or
            employers</strong> for any purpose other than to find your
            address and phone (and they cannot say why they are calling).
          </li>
          <li>
            <strong>Collectors cannot threaten arrest, lawsuits they will
            not actually file, or wage garnishment they cannot actually
            obtain.</strong>
          </li>
          <li>
            <strong>Collectors cannot lie</strong> about the amount, the
            legal status, or who they are.
          </li>
          <li>
            <strong>You have 30 days to demand verification of the debt</strong>
            {" "}from when you first hear from them in writing.
          </li>
        </ul>

        <h2 id="verification">Send a debt verification letter</h2>
        <p>
          Within 30 days of the collector's first written notice, send a
          debt verification request. Until they verify, they cannot
          continue collection activity. Your letter should ask for:
        </p>
        <ul>
          <li>The original creditor's name and address.</li>
          <li>The original account number.</li>
          <li>The amount of the original debt and the current amount, with itemization.</li>
          <li>Documentation that this collector has the right to collect on this debt.</li>
          <li>The date of the last payment on the original account.</li>
        </ul>
        <p>
          Send certified mail with return receipt. If the collector cannot
          verify, they must stop collection and remove the account from
          your credit report.
        </p>

        <h2 id="dispute">Dispute the debt in writing</h2>
        <p>
          If you believe the debt is not yours, has been paid, or is
          incorrect — say so in writing. The collector must investigate
          and pause collection until they respond.
        </p>
        <p>
          If the debt has been reported to credit bureaus, file a separate
          dispute with each bureau (Equifax, Experian, TransUnion). Under
          the Fair Credit Reporting Act, the bureau must investigate
          within 30 days.
        </p>

        <h2 id="document">Document every contact</h2>
        <p>
          Every call: date, time, agent name, agent's company, what was
          said. Every letter: scanned and saved with the postmark. Every
          email and text: saved.
        </p>
        <p>
          This matters for two reasons. First, the debtor often cannot
          remember the timeline — the documentation refreshes it. Second,
          FDCPA violations come with statutory damages of up to $1,000 per
          consumer plus actual damages and attorney's fees. Documented
          violations are the basis for those claims.
        </p>
        <InlineCta>
          <a href="/welcome">CaseThread</a> records call transcripts and
          files every email and letter to a single timeline — so a pattern
          of FDCPA violations is visible as it builds, not buried.
        </InlineCta>

        <h2 id="cease">Cease and desist letters</h2>
        <p>
          You can demand that a collector stop contacting you entirely.
          Send a written letter stating: "Pursuant to 15 USC 1692c(c), I
          require you to cease all communication with me regarding this
          alleged debt, except as expressly permitted by that section."
        </p>
        <p>
          After receipt, the collector can only contact you to confirm
          they have stopped, or to notify you that they intend to file a
          specific legal action. Continued contact is a violation.
        </p>
        <p>
          A cease and desist does not make the debt go away. The collector
          can still sue if the debt is valid and within the statute of
          limitations. But it stops the harassment, gives you breathing
          room, and creates a clear record.
        </p>

        <h2 id="violations">Reporting violations</h2>
        <ul>
          <li>
            <strong>CFPB</strong> — file at consumerfinance.gov. The
            collector must respond.
          </li>
          <li>
            <strong>FTC</strong> — file at reportfraud.ftc.gov. Pattern
            data drives enforcement.
          </li>
          <li>
            <strong>Your state attorney general</strong> — many state AGs
            actively pursue collection abuse.
          </li>
          <li>
            <strong>Private lawsuit</strong> — the FDCPA allows individual
            suits with statutory damages and fee-shifting. Many consumer
            attorneys take these on contingency.
          </li>
        </ul>

        <h2 id="valid">If the debt is yours</h2>
        <p>
          If verification confirms a valid debt you can afford to address,
          you have options:
        </p>
        <ul>
          <li>
            <strong>Settlement.</strong> Collectors routinely accept 30 to
            50 percent of the balance to close the account. Get any
            agreement in writing before paying.
          </li>
          <li>
            <strong>Payment plan.</strong> Workable, but get it in writing
            and confirm the plan does not restart the statute of
            limitations.
          </li>
          <li>
            <strong>Statute of limitations.</strong> If the debt is past
            your state's limit, the collector cannot sue. But making any
            payment may restart the clock — be careful.
          </li>
          <li>
            <strong>Bankruptcy</strong> for unsustainable total debt
            burdens. Worth a free consultation with a consumer
            bankruptcy attorney.
          </li>
        </ul>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "escalate-complaint-to-regulator",
    title: "When and How to Escalate a Complaint to a Regulator",
    description:
      "Which regulator covers which industry, what to include in any complaint, and what realistically happens after you file.",
    summary:
      "A regulator complaint is the lever most companies actually fear. Here is how to pick the right one and file in a way that gets results.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "which", label: "Which regulator for which industry" },
      { id: "cfpb", label: "CFPB — financial products" },
      { id: "ftc", label: "FTC — consumer fraud and deceptive practices" },
      { id: "state-ag", label: "State Attorney General" },
      { id: "insurance", label: "State insurance commissioner" },
      { id: "ncua-fdic-occ", label: "NCUA, FDIC, OCC — banks and credit unions" },
      { id: "include", label: "What to include in any complaint" },
      { id: "after", label: "What happens after you file" },
    ],
    body: () => (
      <>
        <p>
          The threat of a regulator complaint moves cases faster than
          almost anything else available to a consumer. Companies dedicate
          teams to regulator response because regulators have authority no
          customer call has — to demand answers, to assess fines, and to
          publish patterns. Picking the right one and filing properly is
          the difference between a complaint that gets a response and one
          that vanishes into a queue.
        </p>

        <h2 id="which">Which regulator for which industry</h2>
        <ul>
          <li>
            <strong>Banks, credit cards, mortgages, debt collection</strong>
            {" "}— Consumer Financial Protection Bureau (CFPB).
          </li>
          <li>
            <strong>Investment advisors, brokerages</strong> — Securities
            and Exchange Commission (SEC), or FINRA.
          </li>
          <li>
            <strong>Insurance</strong> — your state insurance commissioner.
          </li>
          <li>
            <strong>Health insurance (employer plans)</strong> — U.S.
            Department of Labor (Employee Benefits Security Administration).
          </li>
          <li>
            <strong>Telecommunications</strong> — Federal Communications
            Commission (FCC).
          </li>
          <li>
            <strong>Airlines</strong> — U.S. Department of Transportation.
          </li>
          <li>
            <strong>Cars (safety)</strong> — National Highway Traffic
            Safety Administration (NHTSA).
          </li>
          <li>
            <strong>Cars (warranty/lemon laws)</strong> — your state
            attorney general.
          </li>
          <li>
            <strong>Healthcare providers</strong> — your state medical
            board, state department of health.
          </li>
          <li>
            <strong>Utilities</strong> — state public utility commission.
          </li>
          <li>
            <strong>Landlords</strong> — local housing authority, state
            attorney general (for tenant protection violations).
          </li>
          <li>
            <strong>General consumer fraud</strong> — Federal Trade
            Commission (FTC), state attorney general, Better Business
            Bureau (private but useful).
          </li>
        </ul>

        <h2 id="cfpb">CFPB — financial products</h2>
        <p>
          The Consumer Financial Protection Bureau accepts complaints on
          banking, credit cards, mortgages, auto loans, student loans,
          debt collection, and credit reporting. Filing is at
          consumerfinance.gov. Companies must respond within 15 days. The
          response is published in the public CFPB complaints database — a
          reputational lever many companies want to avoid.
        </p>

        <h2 id="ftc">FTC — consumer fraud and deceptive practices</h2>
        <p>
          The Federal Trade Commission accepts complaints on consumer
          fraud, identity theft, and deceptive business practices. The FTC
          does not resolve individual cases but uses complaint patterns to
          drive enforcement actions. File at reportfraud.ftc.gov.
        </p>
        <p>
          For identity theft specifically, file at identitytheft.gov — the
          report becomes a legal document you can use with creditors and
          credit bureaus.
        </p>

        <h2 id="state-ag">State Attorney General</h2>
        <p>
          Every state attorney general has a consumer protection division
          that handles complaints across industries. State AGs often
          contact companies directly on individual complaints, which most
          companies treat as priority resolutions. State AGs also have
          authority to bring class-action-style enforcement when patterns
          emerge.
        </p>
        <p>
          Search "[your state] attorney general consumer complaint". The
          forms are usually online and brief.
        </p>

        <h2 id="insurance">State insurance commissioner</h2>
        <p>
          For any insurance dispute — auto, home, health, life, disability
          — your state insurance commissioner is the relevant regulator.
          Insurers must respond to commissioner inquiries within
          state-defined timeframes (usually 15 to 30 days). The complaint
          process is free and the form is short.
        </p>

        <h2 id="ncua-fdic-occ">NCUA, FDIC, OCC — banks and credit unions</h2>
        <p>
          For banks, the relevant prudential regulator depends on the
          bank's charter:
        </p>
        <ul>
          <li>
            <strong>National Credit Union Administration (NCUA)</strong> —
            federal credit unions.
          </li>
          <li>
            <strong>Office of the Comptroller of the Currency (OCC)</strong>
            {" "}— national banks (those with "N.A." in the name).
          </li>
          <li>
            <strong>Federal Deposit Insurance Corporation (FDIC)</strong>
            {" "}— state-chartered banks not in the Federal Reserve System.
          </li>
          <li>
            <strong>State banking regulator</strong> — state-chartered
            credit unions and other state banks.
          </li>
        </ul>
        <p>
          The CFPB has a routing tool that picks the right regulator for
          the institution if you are unsure.
        </p>

        <h2 id="include">What to include in any complaint</h2>
        <ol>
          <li>
            Your full contact information and account number.
          </li>
          <li>
            The company's full legal name and address.
          </li>
          <li>
            A clear chronology of events with specific dates and dollar
            amounts.
          </li>
          <li>
            What you have already done to resolve it (calls, letters,
            internal complaints).
          </li>
          <li>
            The company's response (or lack of response) so far.
          </li>
          <li>
            Copies of every relevant document — contracts, statements,
            correspondence.
          </li>
          <li>
            What outcome you want.
          </li>
        </ol>
        <InlineCta>
          <a href="/welcome">CaseThread</a> exports a single PDF dossier
          with chronology, key facts, and correspondence in date order —
          ready to attach to any regulator complaint.
        </InlineCta>

        <h2 id="after">What happens after you file</h2>
        <p>
          The regulator forwards your complaint to the company. The
          company has a regulator-defined window to respond (often 15 to
          30 days). You get a copy of the response.
        </p>
        <p>
          Regulators do not generally award damages or order specific
          settlements — that is what courts are for. What they do is
          create pressure, document patterns, and (for repeat violators)
          bring enforcement actions that can include fines and
          consent decrees. The threat of being added to a regulator's
          enforcement docket is what drives most company responses.
        </p>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "home-repair-contractor-dispute",
    title: "Dealing With a Bad Contractor: A Step-by-Step Guide",
    description:
      "Document the problems, send a written demand, file licensing complaints, claim against the bond, and use small claims if needed.",
    summary:
      "Bad contractor disputes have more leverage than people realize. Licensing boards, surety bonds, and mechanic's liens all create pressure points.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "document", label: "Document the problems" },
      { id: "demand", label: "Send a written demand" },
      { id: "lien", label: "Mechanic's liens — yours and theirs" },
      { id: "licensing", label: "File with the licensing board" },
      { id: "small-claims", label: "Small claims court" },
      { id: "bonding", label: "Bonding and insurance claims" },
      { id: "paper-trail", label: "Why the paper trail matters" },
    ],
    body: () => (
      <>
        <p>
          A bad contractor leaves you with two problems: an unfinished or
          defective project and a counterparty who has already been paid
          and has every incentive to walk away. The leverage exists, but
          you have to know which levers to pull and in what order.
          Licensing boards, surety bonds, and mechanic's liens are the
          mechanisms that change the cost calculation on the contractor's
          side of the table.
        </p>

        <h2 id="document">Document the problems</h2>
        <p>
          Before any escalation, the file needs to be solid:
        </p>
        <ul>
          <li>
            <strong>The signed contract</strong> and any change orders.
          </li>
          <li>
            <strong>Payment records</strong> — every check, transfer, or
            cash receipt.
          </li>
          <li>
            <strong>Photos and video</strong> of every defect, dated.
          </li>
          <li>
            <strong>Independent estimates</strong> from at least two other
            contractors describing the defects and the cost to remedy.
          </li>
          <li>
            <strong>Permit records</strong> from your local building
            department — was the work permitted? Were inspections passed?
          </li>
          <li>
            <strong>All correspondence</strong> — texts, emails, voicemails,
            letters.
          </li>
          <li>
            <strong>Witness statements</strong> if anyone else saw the
            problems.
          </li>
        </ul>

        <h2 id="demand">Send a written demand</h2>
        <p>
          A formal demand letter is the prerequisite for almost every
          escalation that follows. Send it certified mail, return receipt
          requested. The letter should:
        </p>
        <ul>
          <li>Reference the contract and the work performed (or not).</li>
          <li>List the specific defects with dates of discovery.</li>
          <li>State what you require — completion of work, repair of defects, refund of an amount paid.</li>
          <li>Set a deadline (commonly 14 to 30 days).</li>
          <li>State what you will do if the deadline is missed (licensing complaint, bond claim, small claims).</li>
        </ul>
        <p>
          Many contractors who would have ignored a phone call respond to
          a certified demand letter. The letter also becomes Exhibit A in
          every later proceeding.
        </p>

        <h2 id="lien">Mechanic's liens — yours and theirs</h2>
        <p>
          Mechanic's liens are a state-law tool that lets unpaid
          contractors and subcontractors place a lien on your property.
          That cuts two ways:
        </p>
        <ul>
          <li>
            <strong>If the contractor or their subcontractors have not been
            paid</strong> (because you withheld payment for defects),
            expect lien filings. You can dispute these — the lien must be
            timely filed, properly served, and supported by the underlying
            work. Bad contractors often miss procedural requirements.
          </li>
          <li>
            <strong>If you paid the general contractor and they failed to
            pay subs</strong>, the subs can lien your property anyway.
            Many states have "joint check" requirements or sub-payment
            disclosure laws to prevent this — check your state.
          </li>
        </ul>
        <p>
          A title search before you sell or refinance will surface any
          liens, so resolve them rather than ignoring them.
        </p>
        <InlineCta>
          <a href="/welcome">CaseThread</a> keeps every contractor email,
          text, and lien notice indexed and timestamped — so when a
          licensing board or court asks for the chronology, you have it
          ready.
        </InlineCta>

        <h2 id="licensing">File with the licensing board</h2>
        <p>
          In most states, residential contractors must be licensed by a
          state board. The board accepts consumer complaints and can
          investigate, fine, suspend, or revoke licenses. A licensing
          complaint is often more effective than a lawsuit because:
        </p>
        <ul>
          <li>It is free.</li>
          <li>The contractor's livelihood is at stake.</li>
          <li>The board can require restitution as a condition of license retention.</li>
          <li>The board's findings are useful evidence in any later civil case.</li>
        </ul>
        <p>
          Search "[state] contractor license complaint" to find your state
          board. Include in your complaint: the contract, payment records,
          photos of defects, demand letter, the contractor's response
          (or lack of one), and what you want resolved.
        </p>

        <h2 id="small-claims">Small claims court</h2>
        <p>
          For smaller damage amounts (typically under $5,000 to $25,000
          depending on state), small claims is fast and affordable. You
          do not need a lawyer. Bring the contract, payments, photos,
          independent estimates, and the demand letter with proof of
          service.
        </p>
        <p>
          A small claims judgment is enforceable, but collection requires
          additional steps. The judgment itself, on the contractor's
          public record, is often what motivates payment.
        </p>

        <h2 id="bonding">Bonding and insurance claims</h2>
        <p>
          Many states require licensed contractors to carry a surety bond
          (a financial guarantee). When a contractor abandons a job or
          performs defective work, the homeowner can file a claim against
          the bond. The bonding company investigates and pays valid claims
          up to the bond amount, then pursues the contractor for
          reimbursement.
        </p>
        <p>
          Separately, contractors should carry general liability insurance
          for damage they cause to your property. If the contractor caused
          damage (a flood, a fire, structural damage), file a claim with
          their insurer in addition to any bond claim.
        </p>
        <p>
          Bond and insurance amounts vary widely by state and by license
          class. Check your state's contractor licensing site for the
          required bond amount and how to file a claim.
        </p>

        <h2 id="paper-trail">Why the paper trail matters</h2>
        <p>
          Every step above — licensing board, bond claim, small claims
          court — turns on documentation. A coherent file with the
          contract, payment records, photos, independent estimates, and
          dated correspondence wins these proceedings. A pile of receipts
          and "I think it was around June" loses them. The file is the
          case.
        </p>
      </>
    ),
  },
  // ------------------------------------------------------------------
  {
    slug: "get-refund-from-company",
    title: "How to Get a Refund When a Company Won't Cooperate",
    description:
      "The escalation order that works: customer service, executive team, credit card chargeback, BBB, social media, regulator. Step by step.",
    summary:
      "Most refund refusals fold somewhere on the escalation ladder. The trick is using the rungs in the right order — chargeback windows close fast.",
    lastUpdated: "2026-04-23",
    sections: [
      { id: "customer-service", label: "Start with customer service" },
      { id: "executive", label: "Escalate to the executive team" },
      { id: "chargeback", label: "Credit card chargeback" },
      { id: "bbb", label: "Better Business Bureau" },
      { id: "social", label: "Public escalation: social media" },
      { id: "regulatory", label: "Regulatory complaints" },
      { id: "order", label: "Why the order of operations matters" },
    ],
    body: () => (
      <>
        <p>
          The escalation ladder for a refund dispute is well-trodden, but
          most consumers stop at the first or second rung. Companies count
          on it. Knowing every rung — and using them in the right order —
          changes the negotiation. A specific, time-sensitive window
          (the credit card chargeback) sits in the middle of this ladder
          and closes fast, so the order of operations is non-trivial.
        </p>

        <h2 id="customer-service">Start with customer service</h2>
        <p>
          Required, even when you know it will fail. Most regulators and
          chargeback processors expect you to have asked the company
          first. Your first contact should:
        </p>
        <ul>
          <li>
            Use the official channel (phone, chat, email — whatever the
            company publishes).
          </li>
          <li>
            State the issue, the order or transaction reference, and
            the resolution you want (specific dollar amount).
          </li>
          <li>
            Get a case number.
          </li>
          <li>
            End with a written follow-up — even after a phone call —
            confirming what was said and what was promised. Send it from
            an email address you can prove ownership of.
          </li>
        </ul>
        <p>
          Give them a reasonable response window — typically 7 to 14 days
          — before escalating.
        </p>

        <h2 id="executive">Escalate to the executive team</h2>
        <p>
          Almost every consumer-facing company has an executive resolution
          team that exists for one purpose: stopping issues from becoming
          regulator complaints, lawsuits, or social media incidents.
          Reaching them is rarely advertised but consistently effective:
        </p>
        <ul>
          <li>
            Search "[company name] executive customer service" or
            "[company name] CEO email".
          </li>
          <li>
            Most large companies have a published format, e.g.,
            firstname.lastname@company.com.
          </li>
          <li>
            Address the email politely to the CEO or chief customer
            officer. Briefly summarize the issue, the steps you have
            taken, and what would resolve it.
          </li>
          <li>
            The actual response usually comes from a senior agent on the
            executive team, often within a day or two.
          </li>
        </ul>

        <h2 id="chargeback">Credit card chargeback</h2>
        <p>
          If you paid by credit card, you have powerful federal
          protections under the Fair Credit Billing Act. You can dispute
          a charge for goods or services that were not delivered, were
          defective, or were not as described. Key facts:
        </p>
        <ul>
          <li>
            <strong>Time limit.</strong> Generally 60 days from the
            statement date to file. Visa and Mastercard rules give you
            longer in some cases (up to 120 days from delivery for
            non-receipt). After these windows, you lose the right.
          </li>
          <li>
            <strong>How to file.</strong> Call the number on the back of
            your card or use the bank's online portal. Provide the
            transaction details and reason for dispute.
          </li>
          <li>
            <strong>The bank investigates.</strong> They reverse the
            charge provisionally and ask the merchant for evidence the
            sale was valid.
          </li>
          <li>
            <strong>The merchant has time</strong> (typically 30 to 45
            days) to respond. If they cannot prove the sale was valid,
            the chargeback stands.
          </li>
        </ul>
        <p>
          Debit card disputes follow a different process under the
          Electronic Fund Transfer Act, with shorter timelines and weaker
          protections. Always pay with credit when possible if there is
          any chance of a dispute.
        </p>
        <InlineCta>
          <a href="/welcome">CaseThread</a> keeps every email, screenshot,
          and call transcript organized — so when the bank asks for
          evidence at chargeback time, you can attach it in minutes
          rather than reconstructing weeks of contact.
        </InlineCta>

        <h2 id="bbb">Better Business Bureau</h2>
        <p>
          The BBB is not a government regulator — it is a private
          nonprofit. But many companies treat BBB complaints seriously
          because their BBB rating affects new customer acquisition.
          Filing is free, the form is short, and most companies respond
          within a couple of weeks.
        </p>
        <p>
          A BBB complaint is most useful for medium-sized regional
          businesses. Large national chains often have BBB processes that
          produce template responses without resolution; smaller local
          businesses are more likely to engage substantively.
        </p>

        <h2 id="social">Public escalation: social media</h2>
        <p>
          A clear, factual, dated post on the company's public social
          channels (Twitter/X, Facebook, LinkedIn) often produces a
          response within hours. The framework that works:
        </p>
        <ul>
          <li>State the facts. No insults, no exaggeration.</li>
          <li>Tag the company's official handle.</li>
          <li>Reference your case number so the social team can pick up the file.</li>
          <li>State what would resolve it.</li>
        </ul>
        <p>
          Social escalation is most effective for service failures with a
          clear, sympathetic story. It is least effective for billing
          disputes that require nuanced explanation.
        </p>

        <h2 id="regulatory">Regulatory complaints</h2>
        <p>
          If everything above fails, file with the relevant regulator:
        </p>
        <ul>
          <li>
            <strong>FTC and state attorney general</strong> for general
            consumer fraud or deceptive practices.
          </li>
          <li>
            <strong>CFPB</strong> for any financial product.
          </li>
          <li>
            <strong>State insurance commissioner</strong> for insurance.
          </li>
          <li>
            <strong>FCC, NHTSA, DOT, etc.</strong> for industry-specific
            issues.
          </li>
        </ul>
        <p>
          A separate guide on this site walks through{" "}
          <a href="/guides/escalate-complaint-to-regulator">
            picking the right regulator
          </a>
          .
        </p>

        <h2 id="order">Why the order of operations matters</h2>
        <p>
          The chargeback window closes fast — usually within 60 to 120
          days of the transaction. If you spend three months arguing with
          customer service before considering a chargeback, you may have
          already lost the most reliable lever you had. The right order:
        </p>
        <ol>
          <li>Customer service (1 to 2 weeks).</li>
          <li>Executive escalation in parallel (1 to 2 weeks).</li>
          <li>
            Chargeback if paid by credit card and the company has not
            resolved it (well within the window).
          </li>
          <li>BBB and social media in parallel.</li>
          <li>Regulatory complaints as the long-tail option.</li>
        </ol>
        <p>
          Track the dates throughout. Refund disputes have a half-life —
          the longer you wait, the harder the resolution. A clean
          chronology and prompt action win.
        </p>
      </>
    ),
  },
];

export function findGuide(slug: string): Guide | null {
  return GUIDES.find((g) => g.slug === slug) ?? null;
}
